/**
 * AVBP Portal — rebuild router.
 *
 * Sprint 1–5 built the design system, role shell, Gmail/Apple auth,
 * Project Conversation + Review Queue, the engagementTemplate engine and
 * the Project Ledger + money gates. Phase-1 STEP 6 adds the role-based
 * track screens (Admin · Staff · Client) and STEP 7 the ancillary
 * objects. All consume the Sprint 1–5 libs — none are forked.
 *
 * Route gating uses the locked role model (roles.ts): Admin · Staff ·
 * Client tracks; HR / Partner / External-Consultant are real login roles
 * whose dedicated screens are owner-locked LATER sprints (they resolve to
 * the reserved placeholder). The pre-rebuild router lives in src/_legacy/.
 */
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AuthProvider } from './lib/auth';
import { VisibilityProvider } from './lib/visibilityContext';
import { PageChromeProvider } from './lib/pageChromeContext';
import { ToastProvider } from './components/Toast';
import RequireAuth from './components/RequireAuth';
import AppShell from './components/AppShell';
import Login from './routes/Login';
import Logout from './routes/Logout';
import IntakeLanding from './routes/IntakeLanding';
import IntakePreview from './routes/IntakePreview';
import ReportsPreview from './routes/ReportsPreview';
import EvalPreview from './routes/EvalPreview';
import EvalFitPreview from './routes/EvalFitPreview';
import CommsLens from './routes/CommsLens';
import Home from './routes/Home';
import DesignSystem from './routes/DesignSystem';
import Conversation from './routes/Conversation';
import ClientDetail from './routes/ClientDetail';
import ClientNew from './_legacy/routes/ClientNew';
import Contacts from './routes/Contacts';
import ContactDetail from './routes/ContactDetail';
import PartnerDetail from './routes/PartnerDetail';
import AllProjects from './routes/AllProjects';
// Leads "Lenses" pilot (AD-046) — flag-gated swap; flag-off renders today's
// <Leads/> (the swap lives inside LeadsGate so the live screen is untouched).
import LeadsGate from './routes/LeadsGate';
import Quotes from './routes/Quotes';
import Messages from './routes/Messages';
import { TeamInboxGate } from './features/teaminbox';
import ProjectDetail from './routes/ProjectDetail';
import Money from './routes/Money';
import Workload from './routes/Workload';
import Journey from './routes/Journey';
import Report from './routes/Report';
import Intake from './routes/Intake';
import Ancillary from './routes/Ancillary';
import AdminActionQueue from './routes/AdminActionQueue';
import AllTasks from './routes/AllTasks';
import Trash from './routes/Trash';
import SettingsStaffRoles from './routes/SettingsStaffRoles';
import SettingsRateCard from './routes/SettingsRateCard';
import SettingsVisibility from './routes/SettingsVisibility';
import Settings from './routes/Settings';
import Families from './routes/Families';
import GroupDetail from './routes/GroupDetail';
import Properties from './routes/Properties';
import PropertyDetail from './routes/PropertyDetail';
import EvaluateProperty from './routes/EvaluateProperty';
import Pay from './routes/Pay';
import PaymentConfirmed from './routes/PaymentConfirmed';
// Consult Door B — the PUBLIC, no-login self-serve booking page. Additive,
// collision-free: brand-new files under features/consulting/doorb + two public
// routes below (no shared-page edits). Wired to App-Check-gated public CFs via
// a clean seam; degrades honestly until they deploy. (spec 2026-07-08 §4.)
import BookConsultRoute from './features/consulting/doorb/BookConsultRoute';
// Consult Door B — host-side Consultations management (admin/staff, in-shell).
import ConsultationsAdminRoute from './features/consulting/admin/ConsultationsAdminRoute';
// Consult Door A — team-only "Book for client" landing (the Twenty CRM button
// opens /book-consult?contact=<cid>). Loads the client master + mounts the
// existing live BookConsultPanel. Admin/staff, in-shell (same block as below).
import BookForClientRoute from './features/consulting/doora/BookForClientRoute';
import NotFound from './routes/NotFound';

/**
 * Redirect a legacy /conversation/:pid bookmark to the project detail page,
 * where the thread panel now lives (M3.2). React Router v6 <Navigate to>
 * does not interpolate :params, so we read the param and build the target.
 */
function ConversationRedirect() {
  const { pid } = useParams<{ pid: string }>();
  return <Navigate to={pid ? `/project/${pid}` : '/messages'} replace />;
}

export default function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <VisibilityProvider>
        {/* PageChromeProvider wraps BrowserRouter so BOTH the global AppShell
            chrome and every route are inside it — a detail page publishes its
            breadcrumb trail up to AppShell's top-left `.crumb` region. */}
        <PageChromeProvider>
        <BrowserRouter>
          <Routes>
            {/* Public — no shell */}
            <Route path="/login" element={<Login />} />
            {/* Cross-subdomain LOGOUT landing. HR / Capture redirect here
                (`/logout?return=<their URL>`) after clearing their own local
                session so the PORTAL's persistent Firebase Auth session is
                ended too — otherwise the still-signed-in portal re-mints an
                SSO handoff token and the user auto-logs back in ("logout
                doesn't stick"). PUBLIC + no shell (like /login); signs out then
                honours `?return` with an AV-origin allowlist. The in-portal
                account-menu sign-out is unchanged, so client logout is
                unaffected. */}
            <Route path="/logout" element={<Logout />} />
            {/* Intake-link landing (M5 Flag B): a client clicks the sent link
                `/intake/:linkId`, signs in via the existing auth, the canonical
                client claim is minted, and they land in their StageEngine form.
                PUBLIC (self-manages sign-in) — must sit OUTSIDE RequireAuth so
                an unauthenticated recipient is not bounced to /login first. */}
            <Route path="/intake/:linkId" element={<IntakeLanding />} />
            {/* AD-040 preview (no login) — a faithful VIEW of the real intake
                forms for owner review on the preview channel. `?tier=L2A` etc.
                Renders the real stage rail + IntakeModule renderers with LOCAL
                state only; nothing saves. PUBLIC (outside RequireAuth). */}
            <Route path="/preview-intake" element={<IntakePreview />} />
            {/* M6 Inc-1 preview (no login) — the Report Composer SHELL with mock
                project data so the owner can SEE the phase/version + tier model.
                `?tier=L3B` etc. Hostname-gated (NotFound on live), nothing saves. */}
            <Route path="/preview-reports" element={<ReportsPreview />} />
            {/* Dedicated eval-workspace SHELL preview (no login, dev/preview-host
                only) — verifies the full-bleed two-row top + slim footer + tabbed
                right pane without portal chrome. Marking needs auth (degraded). */}
            <Route path="/preview-eval" element={<EvalPreview />} />
            <Route path="/preview-eval-fit" element={<EvalFitPreview />} />
            {/* Public PAYMENT PAGE (no login) — a client opens `/pay/:token`
                from the quote link. Fetches the pay-session from the public
                `getPaySession` CF and renders the AV-green checkout (UPI / QR /
                Razorpay / PayPal / bank). MUST sit OUTSIDE RequireAuth — the
                payer is never signed in. */}
            <Route path="/pay/:token" element={<Pay />} />
            {/* Public payment-confirmed landing (no login) — the Razorpay
                payment-link `callback_url` + the PayPal return redirect here
                after checkout. Purely a friendly branded thank-you; the webhook
                reconciles the payment server-side. Robust to missing params. */}
            <Route path="/payment-confirmed" element={<PaymentConfirmed />} />
            {/* Public CONSULT BOOKING PAGE (no login) — Door B. A client opens
                a link and books a slot themselves. `/book/type/:slug` is the
                evergreen public link (blank form); `/book/:token` is a
                record-specific link (pre-filled, editable). MUST sit OUTSIDE
                RequireAuth — the client is never signed in. `?preview=1` uses
                mock data for QA before the Cloud Functions deploy. */}
            {/* `/book/manage?t=<token>` — the emailed / WhatsApp manage-booking
                link. Static segment (ranks above `/book/:token`), reads the
                signed token from `?t=` and opens the reschedule/cancel screen. */}
            <Route path="/book/manage" element={<BookConsultRoute />} />
            <Route path="/book/type/:slug" element={<BookConsultRoute />} />
            <Route path="/book/:token" element={<BookConsultRoute />} />

            {/* Authenticated — inside the role-based shell */}
            <Route element={<RequireAuth />}>
              <Route element={<AppShell />}>
                {/* Role-based home (Admin/Staff/Client tracks resolve inside) */}
                <Route path="/" element={<Home />} />
                <Route path="/design-system" element={<DesignSystem />} />
                {/* M3.2 retired the standalone Conversation surface — the
                    thread now lives in the right panel of each Project /
                    Contact. /conversation* now redirect so any bookmarked
                    link lands on the project (or Team Inbox) instead of a
                    dead duplicate surface. Kept for one release.
                    /queue remains the Review-Queue alias (separate concern). */}
                <Route path="/conversation/:pid" element={<ConversationRedirect />} />
                <Route path="/conversation" element={<Navigate to="/messages" replace />} />
                <Route path="/queue" element={<Conversation />} />
                {/* Client track */}
                {/* L2B Report-1 intake (Step-2 Slice 1). Any signed-in user
                    reaches it: a CLIENT (isClient session) fills/edits their
                    OWN intake; a TEAM member picks a client and fills on their
                    behalf (the demoable staff path until client OTP sign-in is
                    wired). Both write clients/{id}/intake/L2B_R1 in the
                    engine's answers.* shape (merge:true so Slices 2-4 add the
                    staff/technical fields to the same doc later). */}
                <Route path="/intake" element={<Intake />} />
                <Route path="/journey" element={<Journey />} />
                <Route path="/report" element={<Report />} />
                <Route path="/settings" element={<Settings />} />
              </Route>
            </Route>

            {/* Admin + Staff only — track screens with internal surfaces */}
            <Route element={<RequireAuth roles={['admin', 'staff']} />}>
              <Route element={<AppShell />}>
                <Route path="/leads" element={<LeadsGate />} />
                {/* Phase 3b — the full-page 3-column record. SAME LeadsGate
                    element as /leads, so React Router keeps LeadsLenses
                    MOUNTED across /leads ↔ /leads/:id (selection, data
                    subscriptions, board scroll persist). LeadsLenses reads the
                    :id and switches shape; flag-OFF ignores it (renders the
                    unchanged <Leads/>). Same admin+staff → AppShell nesting. */}
                <Route path="/leads/:id" element={<LeadsGate />} />
                {/* QUOTE MODULE (2026-06-16) — the full quote surface
                    (list/record · detailed builder · send · accept→agreed
                    price). Admin + staff only, like Leads. */}
                <Route path="/quotes" element={<Quotes />} />
                {/* WhatsApp two-way workspace. Admin + Staff only (this
                    block is roles={['admin','staff']}); the callable
                    additionally filters staff to their assigned projects
                    and rejects any client token. Clients have no nav entry
                    and no route — conversation-visibility model. */}
                {/* WhatsApp Inbox (renamed from "Messages" — IA fix Q3).
                    The route path stays /messages so existing deep links +
                    the Messages "Open project ↗" links keep resolving;
                    only the screen LABEL changed. */}
                {/* WA Team Inbox V2 (2026-06-12): flag-gated swap — settings/flags.teaminbox,
                    default OFF → fallback renders today's Messages screen byte-identical. */}
                <Route path="/messages" element={<TeamInboxGate fallback={<Messages />} />} />
                {/* MODULE 2 NAV (2026-06-17) — /contacts is the new first-class
                    Contacts screen (all participants, filter tabs). The param
                    is named :clientId so ContactDetail's re-exported
                    ClientDetail can read it via useParams() unchanged. */}
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/contacts/:clientId" element={<ContactDetail />} />
                {/* Redirects — old /clients and /partners paths forward to
                    the Contacts screen with the appropriate role filter tab.
                    Deep links to /client/:id and /partner/:id stay alive
                    (they are still registered below and render the detail). */}
                <Route
                  path="/clients"
                  element={<Navigate to="/contacts?role=clients" replace />}
                />
                <Route
                  path="/partners"
                  element={<Navigate to="/contacts?role=partners" replace />}
                />
                {/* IA FIX (AVBP_DATA_MODEL_IA_SPEC_v1): /client/:clientId is
                    the legacy Client Detail path — kept alive so existing deep
                    links, the "← All clients" back link in ClientDetail, and
                    tests all continue to resolve. New entry point is
                    /contacts/:clientId above. */}
                <Route path="/client/:clientId" element={<ClientDetail />} />
                <Route path="/clients/new" element={<ClientNew />} />
                {/* PARTNER SPRINT (AVBP_DATA_MODEL_IA_SPEC_v1 §2/Q6):
                    /partner/:partnerId is the Partner Portfolio (a partner
                    + their projects + billing rollup). Admin + staff only —
                    the partner LOGIN role is owner-locked to Phase 6. */}
                <Route path="/partner/:partnerId" element={<PartnerDetail />} />
                <Route path="/all-projects" element={<AllProjects />} />
                <Route path="/project/:pid" element={<ProjectDetail />} />
                {/* Consult Door B — HOST-SIDE management. The staff/admin
                    "Consultations" list (team-only `consultations` read) with
                    per-row reschedule/cancel via Mack's team-auth callables.
                    Admin + staff, inside AppShell (this block). `?preview=1`
                    uses sample data for QA before the callables deploy. */}
                <Route path="/consultations" element={<ConsultationsAdminRoute />} />
                {/* Consult Door A — team-only "Book for client" landing. The
                    Twenty CRM "Book for client" button opens
                    /book-consult?contact=<cid>; this route loads clients/{cid}
                    via getClient and mounts the existing live BookConsultPanel
                    (real Google Calendar + Meet + confirmations). Admin+staff,
                    in-shell (this block), gated further by the `consulting`
                    flag exactly like the /leads/:id Book tab. */}
                <Route path="/book-consult" element={<BookForClientRoute />} />
                <Route path="/ancillary" element={<Ancillary />} />
                {/* MODULE 2 — Family/Organization Groups (2026-06-17).
                    Admin + staff only — consistent with Contacts gating.
                    HR / Partner / External-Consultant have no access. */}
                <Route path="/families" element={<Families />} />
                {/* Full-page GROUP record (P1, 2026-07-06) — replaces the old
                    in-list accordion. /families = list; /families/:id = record. */}
                <Route path="/families/:id" element={<GroupDetail />} />
                {/* MODULE 4 — Property entity (AD-013, 2026-06-18).
                    Admin + staff only — consistent with Contacts gating.
                    /properties = list; /properties/:propertyId = detail. */}
                <Route path="/properties" element={<Properties />} />
                <Route path="/properties/:propertyId" element={<PropertyDetail />} />
                {/* Per-Property Evaluation Workspace (Phase 1, 2026-06-28) — a
                    deep-linkable full-width marking workspace for ONE property
                    under ONE engagement. Stable IDs only (clientId · projectId ·
                    propertyId); ?floorId selects the surface. Mounts the existing
                    marking surface (StaffTechnicalPanel) in two-pane chrome with
                    in-frame zoom/pan. Admin + staff (this block). */}
                <Route
                  path="/evaluate/:clientId/:projectId/:propertyId"
                  element={<EvaluateProperty />}
                />
              </Route>
            </Route>

            {/* P3 · Unified Communications Timeline — Admin + Staff only.
                A comms surface exposes client messages, so it MUST be behind
                staff auth (same RequireAuth roles guard as Contacts/Projects).
                Target is route-driven (contactId / projectId), never hardcoded.
                Standalone chrome (the screen renders its own header + composer),
                so it is gated WITHOUT AppShell to avoid double chrome. */}
            <Route element={<RequireAuth roles={['admin', 'staff']} />}>
              <Route path="/comms/contact/:contactId" element={<CommsLens />} />
              <Route path="/comms/project/:projectId" element={<CommsLens />} />
            </Route>

            {/* Admin-only — money + workload (cross-project, money internals) */}
            <Route element={<RequireAuth roles={['admin']} />}>
              <Route element={<AppShell />}>
                <Route path="/money" element={<Money />} />
                <Route path="/workload" element={<Workload />} />
                {/* Build B — admin task-oversight ("All Tasks", by
                    assignee). Reads the REAL top-level tasks/ collection
                    the AI assistant writes to. Capability tasks.view.all
                    is admin-only; the route is admin-gated to match. */}
                <Route path="/all-tasks" element={<AllTasks />} />
                {/* Recoverable Trash (2026-05-22) — trashed leads /
                    clients / projects / partners: restore or permanently
                    delete. Admin-only (capability trash.manage defaults to
                    admin); the screen also re-checks canManageTrash. */}
                <Route path="/trash" element={<Trash />} />
                {/* Admin-only Settings → Staff Roles. Gate is the route
                    (roles=['admin']); non-admins never reach it and the
                    /settings landing only shows the link to admins. */}
                <Route
                  path="/settings/staff-roles"
                  element={<SettingsStaffRoles />}
                />
                {/* Quotes RESTORE — Stage 1: the editable INR + USD rate
                    card (services/ catalogue). Admin-only — gated to
                    roles=['admin'] like Staff Roles; the /settings landing
                    only shows the link to admins. Writes to services/ are
                    already admin-allowed by firestore.rules (no rules
                    change). */}
                <Route
                  path="/settings/rate-card"
                  element={<SettingsRateCard />}
                />
                {/* Portal Visibility Matrix — controls what each role sees
                    on a project. Admin-only (gate is the route + Settings
                    landing only renders the link for admins). Phase 4
                    Module 4 AD-013. */}
                <Route
                  path="/settings/portal-access"
                  element={<SettingsVisibility />}
                />
                {/* Owner batch-review of HIGH-risk WhatsApp admin commands
                    queued while away (durable queue, owner-away resilience).
                    Admin-only — clients/staff never see it. */}
                <Route
                  path="/admin-queue"
                  element={<AdminActionQueue />}
                />
              </Route>
            </Route>

            <Route path="/index.html" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        </PageChromeProvider>
        </VisibilityProvider>
      </AuthProvider>
    </ToastProvider>
  );
}
